<template>
    <div class="preview-stage w-full max-w-4xl mx-auto">
        <!-- Empty / No File State -->
        <div v-if="!url" class="empty-state">
            <div
                class="glass-card flex flex-col items-center justify-center p-12 text-center border-2 border-dashed border-slate-300 rounded-2xl bg-slate-50/50">
                <div class="icon-pulse mb-4 bg-slate-200 p-4 rounded-full">
                    <FileQuestion class="w-12 h-12 text-slate-400" />
                </div>
                <h3 class="text-xl font-semibold text-slate-700">No file selected</h3>
                <p class="text-slate-500 max-w-xs mt-2">Upload or provide a URL to see a live preview of your media.</p>
            </div>
        </div>

        <!-- Active Preview Stage -->
        <div v-else
            class="preview-content group relative overflow-hidden rounded-2xl shadow-2xl bg-white border border-slate-100 transition-all duration-300">

            <!-- Top Utility Bar (Modern touch) -->
            <div
                class="absolute top-0 inset-x-0 h-12 bg-gradient-to-b from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity z-10 flex items-center justify-end px-4">
                <a :href="url" target="_blank"
                    class="text-white bg-white/20 hover:bg-white/30 backdrop-blur-md p-1.5 rounded-lg transition">
                    <ExternalLink class="w-4 h-4" />
                </a>
            </div>

            <!-- Image Preview -->
            <div v-if="fileType === 'image'" class="flex justify-center bg-slate-50">
                <img :src="url"
                    class="max-w-full h-auto object-contain max-h-[70vh] hover:scale-[1.02] transition-transform duration-500"
                    alt="Preview" />
            </div>

            <!-- Video Preview -->
            <div v-else-if="fileType === 'video'" class="aspect-video bg-black flex items-center">
                <video controls class="w-full h-full">
                    <source :src="url" />
                </video>
            </div>

            <!-- Audio Preview -->
            <div v-else-if="fileType === 'audio'"
                class="p-8 bg-gradient-to-br from-indigo-50 to-white flex flex-col items-center">
                <div class="mb-6 p-6 bg-white shadow-xl rounded-2xl">
                    <Music class="w-16 h-16 text-indigo-500" />
                </div>
                <audio controls class="w-full max-w-md">
                    <source :src="url" />
                </audio>
            </div>

            <!-- Document Preview -->
            <div v-else-if="fileType === 'pdf'" class="bg-slate-200">
                <iframe :src="url" class="w-full h-[75vh] rounded-b-2xl border-none"></iframe>
            </div>

            <!-- Unsupported / Fallback -->
            <div v-else class="p-16 flex flex-col items-center justify-center text-center bg-slate-50">
                <div class="mb-4 bg-amber-100 p-4 rounded-2xl text-amber-600">
                    <FileWarning class="w-10 h-10" />
                </div>
                <h4 class="text-lg font-bold text-slate-800">Unsupported Preview</h4>
                <p class="text-slate-500 mb-6 italic text-sm truncate max-w-xs">{{ fileName }}</p>
                <a :href="url" download
                    class="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 shadow-lg transition transform active:scale-95">
                    <Download class="w-4 h-4" />
                    Download to View
                </a>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
    FileQuestion,
    FileWarning,
    Download,
    ExternalLink,
    Music,
    FileText
} from 'lucide-vue-next';

interface Props {
    url?: string;
    name?: string;
}

const props = defineProps<Props>();

// Computed for file extension logic
const fileName = computed(() => props.url?.split('/').pop() || 'Unknown File');

const fileType = computed(() => {
    if (!props.url) return 'none';
    const ext = props.url.split(/[#?]/)[0].split('.').pop()?.toLowerCase();

    const map: Record<string, string[]> = {
        image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif'],
        video: ['mp4', 'webm', 'ogg', 'mov'],
        audio: ['mp3', 'wav', 'm4a', 'aac'],
        pdf: ['pdf'],
    };

    for (const [type, extensions] of Object.entries(map)) {
        if (extensions.includes(ext || '')) return type;
    }
    return 'fallback';
});
</script>

<style scoped>
.glass-card {
    backdrop-filter: blur(8px);
    background: rgba(255, 255, 255, 0.6);
}

.icon-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1;
        transform: scale(1);
    }

    50% {
        opacity: .7;
        transform: scale(1.05);
    }
}

/* Make native audio player look slightly better */
audio::-webkit-media-controls-panel {
    background-color: #f8fafc;
}
</style>